import React, { Component } from "react";
import { connect } from "react-redux";
import * as signalR from '@microsoft/signalr';
import Metrics from "../../components/metrics";
import { APP_CONFIG } from '../../config';
import {
  fetchMetrics,
  fetchMetricsRunState,
  fetchMetricsFilters,
  receiveMetricsProgress,
  resetMetrics
} from "../../redux/actions";

class MetricsPage extends Component {
  hubConnection = null;
  pollingRunId = null;
  pollingTimer = null;
  subscribedRunId = null;
  metricsResultRequested = false;
  applyRequestPending = false;

  componentDidMount() {
    this.connectMetricsHub();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.authToken && this.props.authToken) {
      this.connectMetricsHub();
    }

    if (this.props.metricsRunId !== prevProps.metricsRunId) {
      this.metricsResultRequested = false;
      this.handleRunIdChanged(prevProps.metricsRunId, this.props.metricsRunId);
    }

    if (
      this.props.metricsRunId !== prevProps.metricsRunId ||
      this.props.metricsRunStatus !== prevProps.metricsRunStatus
    ) {
      this.syncRunPolling();
    }

    if (
      this.props.metricsRunStatus === 'completed' &&
      prevProps.metricsRunStatus !== 'completed' &&
      this.props.metricsRunId
    ) {
      this.loadFinalMetrics(this.props.metricsRunId);
    }

    if (
      this.props.metricsRunStatus === 'failed' &&
      prevProps.metricsRunStatus !== 'failed'
    ) {
      this.stopRunPolling();
    }
  }

  componentWillUnmount() {
    this.stopRunPolling();

    if (this.subscribedRunId) {
      this.unsubscribeFromRun(this.subscribedRunId);
    }

    if (this.hubConnection) {
      this.hubConnection.off('MetricsRunUpdate', this.handleMetricsRunUpdate);
      this.hubConnection.stop();
      this.hubConnection = null;
    }
  }

  handleApplyFilters = (filter) => {
    if (this.applyRequestPending || this.props.metricsLoading) {
      return Promise.resolve();
    }

    this.applyRequestPending = true;
    return this.props.loadMetrics(filter)
      .then((result) => {
        this.applyRequestPending = false;
        return result;
      })
      .catch((error) => {
        this.applyRequestPending = false;
        throw error;
      });
  };

  handleFiltersChanged = (filterState) => {
    this.props.loadFilters(filterState);
  };

  handleResetFilters = () => {
    this.props.resetMetrics();
  };

  handleMetricsRunUpdate = (progress) => {
    this.props.onMetricsRunUpdate(progress);
  };

  handleRunIdChanged = (previousRunId, nextRunId) => {
    if (previousRunId && previousRunId !== nextRunId) {
      this.unsubscribeFromRun(previousRunId);
    }

    if (nextRunId) {
      this.subscribeToRun(nextRunId);
      this.props.loadMetricsRunState(nextRunId);
    }
  };

  connectMetricsHub = () => {
    if (this.hubConnection || !this.props.authToken) {
      return;
    }

    const baseUrl = APP_CONFIG.baseURL.replace('/api/', '/refreshhub');
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(baseUrl + `?access_token=${encodeURIComponent(this.props.authToken)}&domain=${encodeURIComponent(window.location.host)}`)
      .configureLogging(signalR.LogLevel.Warning)
      .withAutomaticReconnect()
      .build();

    hubConnection.serverTimeoutInMilliseconds = 60000;
    hubConnection.on('MetricsRunUpdate', this.handleMetricsRunUpdate);
    hubConnection.onreconnected(() => {
      this.subscribeToRun(this.props.metricsRunId);
    });
    hubConnection.onclose(() => {
      this.subscribedRunId = null;
    });

    hubConnection.start()
      .then(() => {
        this.subscribeToRun(this.props.metricsRunId);
      })
      .catch(() => {
        this.subscribedRunId = null;
      });

    this.hubConnection = hubConnection;
  };

  subscribeToRun = async (runId) => {
    if (!runId || !this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.subscribedRunId && this.subscribedRunId !== runId) {
      await this.unsubscribeFromRun(this.subscribedRunId);
    }

    if (this.subscribedRunId === runId) {
      return;
    }

    try {
      await this.hubConnection.invoke('subscribeMetricsRun', runId);
      this.subscribedRunId = runId;
    } catch (error) {
      this.subscribedRunId = null;
    }
  };

  unsubscribeFromRun = async (runId) => {
    if (!runId) {
      return;
    }

    if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) {
      if (this.subscribedRunId === runId) {
        this.subscribedRunId = null;
      }

      return;
    }

    try {
      await this.hubConnection.invoke('unsubscribeMetricsRun', runId);
    } catch (error) {
      // Ignore disconnect races while tearing down subscriptions.
    } finally {
      if (this.subscribedRunId === runId) {
        this.subscribedRunId = null;
      }
    }
  };

  syncRunPolling = () => {
    const { metricsRunId, metricsRunStatus } = this.props;

    if (metricsRunId && [ 'pending', 'running' ].includes(metricsRunStatus)) {
      this.startRunPolling(metricsRunId);
      return;
    }

    this.stopRunPolling();
  };

  startRunPolling = (runId) => {
    if (!runId || (this.pollingRunId === runId && this.pollingTimer)) {
      return;
    }

    this.stopRunPolling();
    this.pollingRunId = runId;
    this.props.loadMetricsRunState(runId);

    this.pollingTimer = setInterval(() => {
      this.props.loadMetricsRunState(runId);
    }, 5000);
  };

  stopRunPolling = () => {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }

    this.pollingRunId = null;
  };

  loadFinalMetrics = (runId) => {
    if (!runId || this.metricsResultRequested) {
      return;
    }

    this.metricsResultRequested = true;
    this.props.loadMetricsRunState(runId).catch(() => {
      this.metricsResultRequested = false;
    });
  };

  render() {
    const {
      translate,
      metrics,
      filters,
      filtersLoading,
      activeLanguage,
      metricsLoading,
      metricsRunProgress,
      metricsError,
      impersonating,
      softwareUpdate,
    } = this.props;

    return (
      <Metrics
        translate={translate}
        metrics={metrics}
        filters={filters}
        filtersLoading={filtersLoading}
        metricsLoading={metricsLoading}
        metricsRunProgress={metricsRunProgress}
        metricsError={metricsError}
        onApplyFilters={this.handleApplyFilters}
        onResetFilters={this.handleResetFilters}
        onFiltersChanged={this.handleFiltersChanged}
        activeLanguage={activeLanguage}
        impersonating={impersonating}
        softwareUpdate={softwareUpdate}
      />
    );
  }
}

const mapStateToProps = state => ({
  metrics: state.metricsReducer.metrics,
  filters: state.metricsReducer.filters,
  filtersLoading: state.metricsReducer.filtersLoading,
  metricsLoading: state.metricsReducer.loading,
  metricsRunId: state.metricsReducer.runId,
  metricsRunStatus: state.metricsReducer.runStatus,
  metricsRunProgress: state.metricsReducer.progress,
  metricsError: state.metricsReducer.error,
  activeLanguage: state.languageReducer.selectedLanguage,
  authToken: state.authReducer.token,
  impersonating: state.userReducer.impersonating,
  softwareUpdate: state.userReducer.softwareUpdate,
});

const mapDispatchToProps = (dispatch, props) => ({
  loadMetrics: (filter) => {
    return dispatch(fetchMetrics(filter));
  },
  loadMetricsRunState: (runId) => {
    return dispatch(fetchMetricsRunState(runId));
  },
  loadFilters: (filterState) => {
    dispatch(fetchMetricsFilters(filterState));
  },
  onMetricsRunUpdate: (progress) => {
    dispatch(receiveMetricsProgress(progress));
  },
  resetMetrics: () => {
    dispatch(resetMetrics());
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MetricsPage);
